// Centralized API Service for all form submissions

const GEOLOCATION_API = 'https://validationapi.thetechmenders.com/api/GeoLocation/GetGeoLocation';
const ADD_LEAD_API = 'https://leadapi.crmbytes.com/api/Lead/AddLead';

/**
 * Get user's geolocation data
 * @returns {Promise<Object>} Geolocation data
 */
export const getGeolocation = async () => {
  try {
    const response = await fetch(GEOLOCATION_API, {
      method: 'GET',
      headers: {
        'accept': 'text/plain'
      }
    });

    const data = await response.json();
    
    if (data.isSuccess) {
      return {
        success: true,
        data: data.data,
        countryCode: data.data.countryCode,
        countryName: data.data.countryName,
        city: data.data.cityName || '',
        state: data.data.regionName || '',
        ipAddress: data.data.ipAddress
      };
    }
    
    return {
      success: false,
      data: null,
      countryCode: 'US',
      countryName: 'United States',
      city: '',
      state: '',
      ipAddress: ''
    };
  } catch (error) {
    console.error('Geolocation API Error:', error);
    return {
      success: false,
      data: null,
      countryCode: 'US',
      countryName: 'United States',
      city: '',
      state: '',
      ipAddress: ''
    };
  }
};

/**
 * Submit lead to CRM API
 * @param {Object} formData - Form data
 * @param {string} formData.name - Full name
 * @param {string} formData.email - Email address
 * @param {string} formData.countryDialCode - Country dial code (e.g., "1", "44")
 * @param {string} formData.country - Country code (e.g., "US", "GB")
 * @param {string} formData.phoneNumber - Phone number (only digits, no country code)
 * @param {string} formData.description - Description/message
 * @param {string} [formData.packageTitle] - Optional package title
 * @param {number} [formData.packageAmount] - Optional package amount
 * @param {Object} [geoLocationData] - Optional geolocation data
 * @returns {Promise<Object>} API response
 */
export const submitLead = async (formData, geoLocationData = null) => {
  try {
    // Get current page URL
    const pageUrl = window.location.href;

    // Prepare payload
    const payload = {
      name: formData.name,
      email: formData.email,
      countryDialCode: parseInt(formData.countryDialCode.replace('+', '')),
      country: formData.country,
      phoneNumber: formData.phoneNumber, // Only numeric
      ipAddress: geoLocationData?.ipAddress || '',
      description: formData.description || '',
      pageUrl: pageUrl,
      city: geoLocationData?.city || '',
      state: geoLocationData?.state || '',
      geoLocation: geoLocationData?.data ? JSON.stringify(geoLocationData.data) : '',
      packageTitle: formData.packageTitle || '',
      packageAmount: formData.packageAmount || 0
    };

    console.log('Submitting lead with payload:', payload);

    const response = await fetch(ADD_LEAD_API, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    const data = await response.json();

    console.log('API Response:', {
      status: response.status,
      data: data
    });
    console.log('Full API Response Data:', JSON.stringify(data, null, 2));

    // Handle 200 - Success
    // Check multiple possible response formats
    if (response.status === 200) {
      // Check if response contains error in body (even with 200 status)
      if (data.statusCode === 401 || data.statusCode === 400) {
        return {
          success: false,
          error: data.message || 'Unauthorized or invalid request',
          statusCode: data.statusCode
        };
      }
      
      // Format 1: { isSuccess: true, data: { customerId: "..." } }
      if (data.isSuccess && data.data?.customerId) {
        const redirectUrl = `/application/main?customerid=${data.data.customerId}`;
        console.log('Success! Creating redirect URL:', redirectUrl);
        return {
          success: true,
          customerId: data.data.customerId,
          message: data.data.message || 'Lead created successfully',
          redirectUrl: redirectUrl
        };
      }
      
      // Format 2: { customerId: "...", ... } (direct in root)
      if (data.customerId) {
        const redirectUrl = `/application/main?customerid=${data.customerId}`;
        console.log('Success! Creating redirect URL:', redirectUrl);
        return {
          success: true,
          customerId: data.customerId,
          message: data.message || 'Lead created successfully',
          redirectUrl: redirectUrl
        };
      }
      
      // Format 3: { customerid: "..." } (lowercase)
      if (data.customerid) {
        const redirectUrl = `/application/main?customerid=${data.customerid}`;
        console.log('Success! Creating redirect URL:', redirectUrl);
        return {
          success: true,
          customerId: data.customerid,
          message: data.message || 'Lead created successfully',
          redirectUrl: redirectUrl
        };
      }
      
      // If 200 but no customerId found, log the issue
      console.error('200 response but no customerId found in:', data);
    }

    // Handle 401 - Unauthorized
    if (response.status === 401) {
      return {
        success: false,
        error: data.message || 'Unauthorized access',
        statusCode: 401
      };
    }

    // Handle 400 - Bad Request
    if (response.status === 400) {
      return {
        success: false,
        error: data.errorMessage || data.data?.message || 'Invalid data submitted',
        statusCode: 400
      };
    }

    // Handle other errors
    return {
      success: false,
      error: data.errorMessage || 'Something went wrong. Please try again.',
      statusCode: response.status
    };

  } catch (error) {
    console.error('Submit Lead API Error:', error);
    return {
      success: false,
      error: 'Network error. Please check your connection and try again.',
      statusCode: 500
    };
  }
};

/**
 * Get country dial code from country code
 * @param {string} countryCode - Country code (e.g., "US", "GB")
 * @returns {string} Dial code (e.g., "+1", "+44")
 */
export const getDialCodeFromCountryCode = (countryCode) => {
  const dialCodeMap = {
    'US': '+1',
    'GB': '+44',
    'IN': '+91',
    'PK': '+92',
    'AU': '+61',
    'CA': '+1',
    'DE': '+49',
    'FR': '+33',
    'IT': '+39',
    'ES': '+34',
    'NL': '+31',
    'BE': '+32',
    'SE': '+46',
    'NO': '+47',
    'DK': '+45',
    'FI': '+358',
    'PL': '+48',
    'CH': '+41',
    'AT': '+43',
    'IE': '+353',
    'NZ': '+64',
    'SG': '+65',
    'MY': '+60',
    'TH': '+66',
    'ID': '+62',
    'PH': '+63',
    'VN': '+84',
    'JP': '+81',
    'KR': '+82',
    'CN': '+86',
    'HK': '+852',
    'TW': '+886',
    'BR': '+55',
    'MX': '+52',
    'AR': '+54',
    'CL': '+56',
    'CO': '+57',
    'PE': '+51',
    'VE': '+58',
    'ZA': '+27',
    'EG': '+20',
    'NG': '+234',
    'KE': '+254',
    'SA': '+966',
    'AE': '+971',
    'IL': '+972',
    'TR': '+90',
    'RU': '+7',
    'UA': '+380',
    'GR': '+30',
    'PT': '+351',
    'CZ': '+420',
    'HU': '+36',
    'RO': '+40',
    'BG': '+359'
  };

  return dialCodeMap[countryCode] || '+1';
};
